import React, { useState, useEffect, useRef } from 'react';
import { UserCheck, Trash2, Upload, Plus, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const API = '/api';

const InPersonLeadsTab = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkForm, setShowBulkForm] = useState(false);
  const [newLead, setNewLead] = useState({ email: '', name: '', notes: '' });
  const [bulkEmails, setBulkEmails] = useState('');
  const [bulkNotes, setBulkNotes] = useState('');
  const [excelNotes, setExcelNotes] = useState('');
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.get(`${API}/inperson-leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data.leads || []);
    } catch (error) {
      console.error('Failed to fetch in-person leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddLead = async (e) => {
    e.preventDefault();
    if (!newLead.email) {
      toast.error('Please enter an email address');
      return;
    }

    const token = localStorage.getItem('admin_token');
    try {
      await axios.post(`${API}/inperson-leads`, newLead, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Added ${newLead.email} to in-person leads`);
      setNewLead({ email: '', name: '', notes: '' });
      setShowAddForm(false);
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add lead');
    }
  };

  const handleBulkAdd = async (e) => {
    e.preventDefault();
    const emails = bulkEmails.split(/[\n,;]+/).map(e => e.trim()).filter(e => e);
    
    if (emails.length === 0) {
      toast.error('Please enter at least one email address');
      return;
    }

    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.post(`${API}/inperson-leads/bulk`, {
        emails,
        notes: bulkNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setBulkEmails('');
      setBulkNotes('');
      setShowBulkForm(false);
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add leads');
    }
  };

  const handleDelete = async (lead) => {
    if (!window.confirm(`Remove ${lead.email} from in-person leads list?`)) return;
    
    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/inperson-leads/${lead.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email removed from list');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to remove email');
    }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setUploadingExcel(true);
    const token = localStorage.getItem('admin_token');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('notes', excelNotes || 'Uploaded from Excel');

      const response = await axios.post(`${API}/inperson-leads/upload-excel`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(response.data.message);
      setExcelNotes('');
      setShowBulkForm(false);
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload Excel file');
    } finally {
      setUploadingExcel(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF4500]"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-[#FF4500]" />
            In-Person Leads
          </h3>
          <p className="text-sm text-[#525252] mt-1">
            Emails of leads who have already had an in-person coaching session. These leads cannot book a "First Inquiry Call" - they can only book a "Next Steps Call".
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { setShowAddForm(true); setShowBulkForm(false); }}
            className="flex items-center gap-2 bg-[#FF4500] text-white px-4 py-2 rounded-lg hover:bg-[#E03E00] transition-all font-semibold text-sm"
          >
            <Plus className="w-4 h-4" />
            Add Single
          </button>
          <button
            onClick={() => { setShowBulkForm(true); setShowAddForm(false); }}
            className="flex items-center gap-2 bg-[#0055FF] text-white px-4 py-2 rounded-lg hover:bg-[#0044CC] transition-all font-semibold text-sm"
          >
            <Upload className="w-4 h-4" />
            Bulk Upload
          </button>
        </div>
      </div>

      {/* Add Single Lead Form */}
      {showAddForm && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <h4 className="font-semibold mb-3">Add Single In-Person Lead</h4>
          <form onSubmit={handleAddLead} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input
                type="email"
                placeholder="Email address *"
                value={newLead.email}
                onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                className="h-10 px-4 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                required
              />
              <input
                type="text"
                placeholder="Name (optional)"
                value={newLead.name}
                onChange={(e) => setNewLead({ ...newLead, name: e.target.value })}
                className="h-10 px-4 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
              />
              <input
                type="text"
                placeholder="Notes (optional)"
                value={newLead.notes}
                onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                className="h-10 px-4 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="bg-[#25D366] text-white px-4 py-2 rounded-lg hover:bg-[#1EBE5A] transition-all font-semibold text-sm"
              >
                Add Lead
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="bg-gray-200 text-[#0A0A0A] px-4 py-2 rounded-lg hover:bg-gray-300 transition-all font-semibold text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Bulk Upload Form */}
      {showBulkForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-blue-800 mb-3">Bulk Upload In-Person Leads</h4>
          
          {/* Excel Upload Section */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <h5 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4" />
              Upload Excel File
            </h5>
            <p className="text-sm text-green-600 mb-3">
              Upload an Excel file (.xlsx) containing email addresses. The system will scan all cells for valid emails.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                placeholder="Notes for all imported emails (optional)"
                value={excelNotes}
                onChange={(e) => setExcelNotes(e.target.value)}
                className="flex-1 h-10 px-4 bg-white border border-green-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <label className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm cursor-pointer transition-all ${
                uploadingExcel 
                  ? 'bg-gray-400 text-white cursor-not-allowed' 
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}>
                {uploadingExcel ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <FileSpreadsheet className="w-4 h-4" />
                    Choose Excel File
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleExcelUpload}
                  disabled={uploadingExcel}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          <div className="relative mb-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-blue-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-blue-50 text-blue-500">Or paste emails manually</span>
            </div>
          </div>

          <p className="text-sm text-blue-600 mb-3">
            Paste multiple email addresses below (one per line, or separated by commas/semicolons).
          </p>
          <form onSubmit={handleBulkAdd} className="space-y-3">
            <textarea
              placeholder="email1@example.com&#10;email2@example.com&#10;email3@example.com"
              value={bulkEmails}
              onChange={(e) => setBulkEmails(e.target.value)}
              className="w-full h-32 px-4 py-3 bg-white border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <input
              type="text"
              placeholder="Notes for all (optional) - e.g., 'In-person session Jan 2026'"
              value={bulkNotes}
              onChange={(e) => setBulkNotes(e.target.value)}
              className="w-full h-10 px-4 bg-white border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex gap-2">
              <button
                type="submit"
                className="bg-[#0055FF] text-white px-4 py-2 rounded-lg hover:bg-[#0044CC] transition-all font-semibold text-sm"
              >
                Upload Text Emails
              </button>
              <button
                type="button"
                onClick={() => setShowBulkForm(false)}
                className="bg-gray-200 text-[#0A0A0A] px-4 py-2 rounded-lg hover:bg-gray-300 transition-all font-semibold text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Leads Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Email</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Name</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Notes</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Added</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((lead) => (
              <tr key={lead.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-4 text-sm font-medium">{lead.email}</td>
                <td className="py-3 px-4 text-sm text-[#525252]">{lead.name || '-'}</td>
                <td className="py-3 px-4 text-sm text-[#525252]">{lead.notes || '-'}</td>
                <td className="py-3 px-4 text-sm text-[#525252]">
                  {lead.added_at ? format(new Date(lead.added_at), 'MMM d, yyyy') : '-'}
                </td>
                <td className="py-3 px-4">
                  <button
                    onClick={() => handleDelete(lead)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Remove from list"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {leads.length === 0 && (
          <p className="text-center text-[#525252] py-8">
            No in-person leads added yet. Use the buttons above to add emails.
          </p>
        )}
      </div>

      {/* Summary */}
      {leads.length > 0 && (
        <div className="mt-4 text-sm text-[#525252]">
          Total: {leads.length} in-person lead{leads.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

export default InPersonLeadsTab;
